/**
 * `… FROM orders PIVOT (SUM(total) FOR ▮` — the pivot column.
 *
 * The clause machine reports this position as an ordinary FROM tail, so the
 * relation-continuation keywords (`AS`, `CROSS`, `FULL`, …) answered it and the
 * source relation's columns never surfaced. The only legal token there is a
 * column of the pivoted relation.
 *
 * UNPIVOT is deliberately excluded: its `FOR ▮` names a NEW column that will
 * hold the pivoted-out names (`UNPIVOT (v FOR metric IN (a, b))`), so the
 * existing columns are exactly the wrong suggestion.
 */

import { scanRegion, slotScanEnd, wordBefore, type ScanOptions } from './slotTextScan';

/**
 * `… PIVOT (SUM(total) FOR line IN (▮[R], [M]))` — the pivot IN-list.
 *
 * The entries are the categorical VALUES of the FOR column, spelled as
 * delimited identifiers because they become output column names. Nothing in
 * the schema belongs here: a relation, view, schema, database or CTE name is
 * never a legal entry, and the source relation's own alias (which the pivot
 * interior otherwise offers as a qualifier) is wrong too — the interior branch
 * uses this to withhold it (mssql script.7 §4).
 */
export function isPivotInListPosition(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): boolean {
  // Scanned to the caret itself, not to `slotScanEnd`: the first entry's caret
  // sits directly after the `(`, which that helper (a keyword-position probe)
  // rejects.
  const inParen = scanRegion(text, statementStart, Math.min(offset, text.length), opts)
    .openParenAt;
  if (inParen === null) return false;
  if (wordBefore(text, inParen, statementStart) !== 'IN') return false;
  // The `IN (` must itself sit inside the `PIVOT (` / `UNPIVOT (` frame.
  const pivotParen = scanRegion(text, statementStart, inParen, opts).openParenAt;
  if (pivotParen === null) return false;
  const opener = wordBefore(text, pivotParen, statementStart);
  return opener === 'PIVOT' || opener === 'UNPIVOT';
}

export function isPivotForColumnPosition(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): boolean {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return false;
  if (wordBefore(text, scanEnd, statementStart) !== 'FOR') return false;
  const openAt = scanRegion(text, statementStart, scanEnd, opts).openParenAt;
  if (openAt === null) return false;
  return wordBefore(text, openAt, statementStart) === 'PIVOT';
}
